import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RoleAssignmentWrite } from '@citibox-panel/api-client';
import { ROLE_ASSIGNMENTS_KEY } from './useRoleAssignments';

// «Quitar todo» is its own request, never an upsert with empty lists: the server must not infer a
// removal from an empty selection.
export type SaveRequest =
  | { kind: 'upsert'; write: RoleAssignmentWrite }
  | { kind: 'remove-all'; email: string; expectedVersion: string };

export interface SaveRoleAssignmentDeps {
  upsert: (write: RoleAssignmentWrite) => Promise<void>;
  removeAll: (email: string, expectedVersion: string) => Promise<void>;
}

// The write's own key, so tests can find it in the mutation cache. On a permission refusal the
// host's handler drops the refused write (target email, roles) by reference, whatever its key.
const ROLE_ASSIGNMENTS_WRITE_KEY = ['role-assignments', 'write'] as const;

// retry: false: a write guarded by a version must never be silently re-sent.
// networkMode 'always': offline is a named failure in the dialog, never a paused, busy dialog.
// onSuccess RETURNS the invalidation, so the mutation stays pending until the refreshed list lands
// and the dialog closes over the new row, not the old one.
export function useSaveRoleAssignment(deps: SaveRoleAssignmentDeps) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ROLE_ASSIGNMENTS_WRITE_KEY,
    mutationFn: (request: SaveRequest) =>
      request.kind === 'upsert' ? deps.upsert(request.write) : deps.removeAll(request.email, request.expectedVersion),
    retry: false,
    networkMode: 'always',
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROLE_ASSIGNMENTS_KEY }),
  });
}
